import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { getRiskType, RISK_RATIO_TO_SLIDER_STEP, RISK_SLIDER_STEP_TO_RATIO } from '../../service';

@Component({
  selector: 'app-inyova-grow-risk-slider',
  templateUrl: './inyova-grow-risk-slider.component.html',
  styleUrls: ['./inyova-grow-risk-slider.component.scss'],
  standalone: false
})
export class InyovaGrowRiskSliderComponent implements OnInit, OnChanges {
  @Input() acceptConsent: boolean;
  @Input() acceptFees: boolean;
  @Input() editGrowRisk: boolean;
  @Input() initialInvestment: number;
  @Input() currency = 'CHF';
  @Input() showConsentError: boolean;
  @Input() showFeesError: boolean;
  @Input() selectedRiskSplit: null | number = null;
  @Input() suggestedRiskSplit: number;
  @Input() marketFundRate: number;
  @Input() minReturnPercentage: number;
  @Input() maxReturnPercentage: number;
  @Input() hideCTA: boolean;

  @Output() riskChange: EventEmitter<{ selectedRisk: number; acceptConsent: boolean; acceptFees: boolean }> = new EventEmitter<{
    selectedRisk: number;
    acceptConsent: boolean;
    acceptFees: boolean;
  }>();

  totalSteps = 5;
  riskSliderStep: number;
  selectedRisk: number;

  returnForecast: {
    minPercentage: number;
    minReturn: number;
    maxPercentage: number;
    maxReturn: number;
  } = {
    minPercentage: 0,
    minReturn: 0,
    maxPercentage: 0,
    maxReturn: 0
  };

  feesConstants: {
    notGreenFee: number;
    greenFee: number;
    prodCostFee: number;
  } = {
    notGreenFee: 0.0001,
    greenFee: 0.0095,
    prodCostFee: 0.0011
  };

  ratioToRiskStep = RISK_RATIO_TO_SLIDER_STEP;
  steptoRatioMap = RISK_SLIDER_STEP_TO_RATIO;

  get totalFee() {
    return this.selectedRisk * this.feesConstants.greenFee + (1 - this.selectedRisk) * this.feesConstants.notGreenFee;
  }

  ngOnInit() {
    this.riskSliderStep = this.ratioToRiskStep[this.selectedRiskSplit !== null ? this.selectedRiskSplit : this.suggestedRiskSplit];
    this.selectedRisk = this.selectedRiskSplit !== null ? this.selectedRiskSplit : this.suggestedRiskSplit;
    this.getForecastMoneyReturn();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.suggestedRiskSplit && !changes.suggestedRiskSplit.firstChange) {
      if (changes.suggestedRiskSplit.currentValue !== this.selectedRisk) {
        this.riskSliderStep = this.ratioToRiskStep[changes.suggestedRiskSplit.currentValue];
        this.selectedRisk = changes.suggestedRiskSplit.currentValue;
        this.acceptConsent = false;
        this.acceptFees = false;
        this.getForecastMoneyReturn();

        this.riskChange.emit({ selectedRisk: this.selectedRisk, acceptConsent: this.acceptConsent, acceptFees: this.acceptFees });
      }
    }

    if (changes && changes.initialInvestment && !changes.initialInvestment.firstChange) {
      this.getForecastMoneyReturn();
    }
  }

  getRiskSliderHeading(step: number) {
    this.selectedRisk = this.steptoRatioMap[step];
    this.getForecastMoneyReturn();

    return getRiskType(step);
  }

  onRiskProfileChange(step: number) {
    this.acceptConsent = false;
    this.acceptFees = false;

    this.riskSliderStep = step;
    this.selectedRisk = this.steptoRatioMap[step];

    this.riskChange.emit({ selectedRisk: this.selectedRisk, acceptConsent: this.acceptConsent, acceptFees: this.acceptFees });
  }

  onToggleConsent() {
    this.acceptConsent = !this.acceptConsent;
    this.riskChange.emit({ selectedRisk: this.selectedRisk, acceptConsent: this.acceptConsent, acceptFees: this.acceptFees });
  }

  onToggleFees() {
    this.acceptFees = !this.acceptFees;
    this.riskChange.emit({ selectedRisk: this.selectedRisk, acceptConsent: this.acceptConsent, acceptFees: this.acceptFees });
  }

  getForecastMoneyReturn() {
    // it's X value
    const minPercentage = this.getFinalPercentage(this.minReturnPercentage);

    // it's XXX value
    const minReturn = this.getFinalReturn(minPercentage);

    // it's Y value
    const maxPercentage = this.getFinalPercentage(this.maxReturnPercentage);

    // it's YYY value
    const maxReturn = this.getFinalReturn(maxPercentage);

    this.returnForecast = {
      minPercentage,
      minReturn,
      maxPercentage,
      maxReturn
    };
  }

  getFinalPercentage(value): number {
    return this.selectedRisk * value + (1 - this.selectedRisk) * this.marketFundRate;
  }

  getFinalReturn(value): number {
    return value * this.initialInvestment;
  }
}
